import { JsonObject, JsonObjectProperty } from 'survey-core';

const CASE_PRIORITIES = [
  {
    urg_code: 'ROU',
    urg_desc: 'Routine'
  },
  {
    urg_code: 'URG',
    urg_desc: 'Urgent'
  }
] as const;

const ACTION_TYPE = 'actionType';

const ACTION_TYPE_CHOICES = [
  'Create task',
  'Book appointment',
  'Go to case review',
  'Book face-to-face consultant',
  'Set urgency',
  'Set discharge reason',
  'Create Case',
  'Setup Card',
  'Set pathway status to case review',
  'Set pathway status to face-to-face',
  'Create Home Test Kit Order',
  'Create In Person Order',
  'Set Lab Test Order Status'
] as const;

type ActionType = (typeof ACTION_TYPE_CHOICES)[number];

const CASE_DISCHARGE_REASONS = [
  {
    discharge_code: 'RECOV',
    discharge_desc: 'Patient Recovered'
  },
  {
    discharge_code: 'IUNHAPPY',
    discharge_desc: 'I am unhappy with the virtual pathway'
  },
  {
    discharge_code: 'INOTWANT',
    discharge_desc: 'I do not wish to continue treatment'
  },
  {
    discharge_code: 'ALT',
    discharge_desc: 'Chose alternative service'
  },
  {
    discharge_code: 'SELF',
    discharge_desc: 'Self management'
  }
] as const;

const RANDOX_ORDER_STATUSES = [{ code: 'NOT_SUITABLE', label: 'Unsuitable Options' }] as const;

export const surveyActionProperties = [
  {
    name: ACTION_TYPE,
    choices: ACTION_TYPE_CHOICES.map((choice) => ({ value: choice, text: choice })),
    category: 'general',
    visibleIndex: 2
  },
  {
    name: 'caseDischargeReason',
    choices: CASE_DISCHARGE_REASONS.map(({ discharge_desc, discharge_code }) => ({
      value: discharge_code,
      text: discharge_desc
    })),
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Set discharge reason',
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'urgencyType',
    choices: CASE_PRIORITIES.map(({ urg_desc, urg_code }) => ({ value: urg_code, text: urg_desc })),
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Set urgency',
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'appointmentType',
    choices: ['New appointment'],
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Book appointment',
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'taskType',
    choices: [
      'Book Appointment',
      'Call patient',
      'Red flag',
      'F2F Physio',
      'F2F Consult',
      'Patient requested follow up',
      'Other'
    ],
    dependsOn: ACTION_TYPE,
    visibleIf: () => false,
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'subSpecialty',
    default: '',
    dependsOn: [ACTION_TYPE, 'appointmentType'],
    visibleIf: (obj) => obj.actionType === 'Book appointment' && obj.appointmentType !== 'Clinical review',
    category: 'general',
    visibleIndex: 4
  },
  {
    name: 'clinicianType',
    choices: ['Physio', 'Advanced Physio', 'Consultant'],
    default: 'Physio',
    dependsOn: [ACTION_TYPE, 'appointmentType'],
    visibleIf: (obj) => obj.actionType === 'Book appointment' && obj.appointmentType !== 'Clinical review',
    category: 'general',
    visibleIndex: 5
  },
  {
    name: 'randoxOrderStatus',
    choices: RANDOX_ORDER_STATUSES.map(({ code, label }) => ({ value: code, text: label })),
    default: 'NOT_SUITABLE',
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Set Lab Test Order Status',
    category: 'general',
    visibleIndex: 6
  },
  {
    name: 'buttonText',
    default: 'OK',
    category: 'general',
    visibleIndex: 10
  }
] satisfies CustomizeSurveyActionJsonObjectProperty[];

/** Renders a custom action button which is used to interpret what outcome is necessary from the survey inputs and leads to the appropriate action on button press. */
const CustomizeNonClass2MedDevSurveyAction = () => {
  const name = 'action';

  if (!JsonObject.metaData.findClass(name)) {
    JsonObject.metaData.addClass(name, [], undefined, 'html');
  }

  JsonObject.metaData.addProperties(name, surveyActionProperties);
  const actionTypeProp = JsonObject.metaData.findProperty(name, 'actionType');
  if (actionTypeProp) {
    actionTypeProp.setChoices([...ACTION_TYPE_CHOICES]);
  }

  const taskTypeProp = JsonObject.metaData.findProperty(name, 'taskType');
  if (taskTypeProp) {
    taskTypeProp.setChoices([
      'Book Appointment',
      'Call patient',
      'Red flag',
      'F2F Physio',
      'F2F Consult',
      'Patient requested follow up',
      'Other'
    ]);
  }
};

export default CustomizeNonClass2MedDevSurveyAction;

type CustomizeSurveyActionJsonObjectProperty = Omit<Partial<JsonObjectProperty>, 'visibleIf'> & {
  choices?: string[] | { value: string; text: string }[];
  visibleIf?: (obj: { actionType: ActionType; appointmentType: string }) => boolean;
};
